import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Space,
    Button,
    message,
    Spin,
    Alert,
    Result,
    Modal,
    Tooltip,
    Input
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    InfoCircleOutlined,
    CrownOutlined,
    ExclamationCircleOutlined,
    QuestionCircleOutlined,
    CopyOutlined,
    LinkOutlined
} from '@ant-design/icons';
import moment from 'moment';
import auctionService from '../../services/auctionService';
import { useSelector } from 'react-redux';
import env from '../../env';

const { Title, Text, Paragraph } = Typography;

const AuctionInvitationPage = () => {
    const { auctionSlug, invitationCode } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);
    const [loading, setLoading] = useState(true);
    const [auction, setAuction] = useState(null);
    const [error, setError] = useState(null);
    const [responding, setResponding] = useState(false);
    const [isAlreadyParticipant, setIsAlreadyParticipant] = useState(false);
    const [helpModalVisible, setHelpModalVisible] = useState(false);
    const isOwner = auction && currentUser?.id === auction.user_id;

    useEffect(() => {
        loadAuctionDetails();
    }, [auctionSlug]);

    const loadAuctionDetails = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getAuctionDetailsWithInvitation(auctionSlug, invitationCode);
            setAuction(response.data);
            setError(null);
        } catch (err) {
            if (err.response?.status === 404) {
                setError('This invitation link is invalid or has already been used. Please contact the auction owner for a new invitation.');
            } else {
                setError(err.response?.data?.message || 'Failed to load auction details. The invitation may be invalid or expired.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResponse = async (status) => {
        if (!currentUser) {
            message.warning('Please log in to respond to the invitation');
            navigate('/login', { state: { from: `/auctions/${auctionSlug}/invitation/${invitationCode}` } });
            return;
        }

        try {
            setResponding(true);
            await auctionService.respondToInvitation(auctionSlug, {
                invitation_code: invitationCode,
                status: status
            });
            
            message.success(`Invitation ${status} successfully`);
            navigate(`/auctions/${auctionSlug}`);
        } catch (err) {
            if (err.response?.data?.message?.includes('Duplicate entry') || 
                err.response?.data?.message?.includes('already exists')) {
                setIsAlreadyParticipant(true);
                message.warning('You are already a participant in this auction');
            } else {
                message.error(err.response?.data?.message || 'Failed to respond to invitation');
            }
        } finally {
            setResponding(false);
        }
    };

    const copyAuctionLink = () => {
        const auctionLink = `${env.FRONTEND_URL}/auctions/${auctionSlug}`;
        navigator.clipboard.writeText(auctionLink).then(() => {
            message.success('Auction link copied to clipboard!');
        }).catch(() => {
            message.error('Failed to copy link');
        });
    };

    if (error) {
        return (
            <div style={{ padding: '50px' }}>
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    if (!auction) {
        return (
            <div style={{ 
                padding: '50px', 
                textAlign: 'center',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Spin size="large" />
                <Text style={{ display: 'block', marginTop: '20px' }}>Loading invitation details...</Text>
            </div>
        );
    }

    if (isOwner) {
        return (
            <div style={{ padding: '50px' }}>
                <Result
                    icon={<CrownOutlined style={{ color: '#1890ff' }} />}
                    status="info"
                    title="You are the Auction Owner"
                    subTitle="As the owner of this auction, you don't need to respond to invitations."
                    extra={[
                        <Button 
                            type="primary" 
                            key="dashboard"
                            onClick={() => navigate('/dashboard')}
                        >
                            Go to Dashboard
                        </Button>,
                        <Button 
                            key="view"
                            onClick={() => navigate(`/auctions/${auctionSlug}`)}
                        >
                            View Auction
                        </Button>
                    ]}
                />
            </div>
        );
    }

    if (isAlreadyParticipant) {
        return (
            <div style={{ padding: '50px' }}>
                <Result
                    icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                    status="warning"
                    title="Already a Participant"
                    subTitle="You are already a participant in this auction. No need to accept the invitation again."
                    extra={[
                        <Button 
                            type="primary" 
                            key="view"
                            onClick={() => navigate(`/auctions/${auctionSlug}`)}
                        >
                            View Auction
                        </Button>
                    ]}
                />
            </div>
        );
    }

    return (
        <div style={{ 
            padding: '20px', 
            minHeight: '80vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
            {/* Invitation Card */}
            <Card 
                style={{ 
                    maxWidth: '500px', 
                    width: '100%',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                    position: 'relative',
                    overflow: 'hidden'
                }}
                bodyStyle={{ padding: 0 }}
            >
                {/* Help Icon */}
                <Tooltip title="View Instructions">
                    <Button
                        type="text"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setHelpModalVisible(true)}
                        style={{
                            position: 'absolute',
                            top: '16px',
                            right: '16px',
                            zIndex: 10,
                            color: '#1890ff',
                            fontSize: '18px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.9)',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                        }}
                    />
                </Tooltip>

                {/* Auction Image */}
                <div style={{ 
                    position: 'relative',
                    height: '200px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                    const overlay = e.currentTarget.querySelector('.hover-overlay');
                    if (overlay) overlay.style.opacity = 1;
                }}
                onMouseLeave={(e) => {
                    const overlay = e.currentTarget.querySelector('.hover-overlay');
                    if (overlay) overlay.style.opacity = 0;
                }}
                >
                    {auction.image ? (
                        <img
                            alt={auction.title}
                            src={env.BACKEND_STORAGE_URL + '/' + auction.image}
                            style={{ 
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                            onMouseOver={(e) => e.target.style.transform = 'scale(1.08)'}
                            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            display: auction.image ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            flexDirection: 'column',
                            gap: '16px'
                        }}
                    >
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '32px',
                            fontWeight: 'bold',
                            backdropFilter: 'blur(10px)'
                        }}>
                            M
                        </div>
                        <Text style={{ 
                            fontSize: '14px', 
                            textAlign: 'center',
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontWeight: 500
                        }}>
                            Maveshibids
                        </Text>
                    </div>
                    <div 
                        className="hover-overlay"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)',
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: 'none'
                        }}
                    />
                    
                    {/* Private Auction badge */}
                    <div style={{
                        position: 'absolute',
                        bottom: 12,
                        left: 14,
                        zIndex: 3,
                        background: 'rgba(0, 0, 0, 0.6)',
                        backdropFilter: 'blur(10px)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <CrownOutlined />
                        Private Auction
                    </div>
                </div>

                {/* Content Section */}
                <div style={{ padding: '24px' }}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Title */}
                        <div style={{ textAlign: 'center' }}>
                            <Title level={3} style={{ margin: '0 0 8px 0', color: '#262626' }}>
                                {auction.title}
                            </Title>
                            {auction.description && (
                                <Text type="secondary" style={{ fontSize: '14px' }}>
                                    {auction.description.length > 100 
                                        ? `${auction.description.substring(0, 100)}...` 
                                        : auction.description}
                                </Text>
                            )}
                        </div>

                        {/* Live Auction Start Time */}
                        <div style={{ 
                            background: 'linear-gradient(135deg, #1890ff, #40a9ff)', 
                            padding: '20px', 
                            borderRadius: '12px',
                            textAlign: 'center',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <div style={{ fontSize: '13px', opacity: 0.9, fontWeight: '500' }}>
                                    Live Auction Start Time
                                </div>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    <ClockCircleOutlined />
                                    {moment(auction.start_time).format('MMMM D, YYYY [at] HH:mm')}
                                </div>
                                <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
                                    {moment(auction.start_time).fromNow()}
                                </div>
                            </Space>
                        </div>

                        {/* Auction Preview Link */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <Text strong style={{ fontSize: '13px', color: '#595959' }}>
                                    <LinkOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
                                    Auction Preview Link
                                </Text>
                                <Tooltip title="Copy link">
                                    <Button
                                        icon={<CopyOutlined />}
                                        onClick={copyAuctionLink}
                                        size="small"
                                        style={{
                                            borderRadius: '8px'
                                        }}
                                    >
                                        Copy
                                    </Button>
                                </Tooltip>
                            </div>
                            <Input
                                readOnly
                                value={`${env.FRONTEND_URL}/auctions/${auctionSlug}`}
                                style={{
                                    fontSize: '12px',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>

                        {/* Action buttons */}
                        <div style={{ textAlign: 'center' }}>
                            <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => handleResponse('accepted')}
                                    loading={responding}
                                    style={{ 
                                        minWidth: '140px',
                                        borderRadius: '8px',
                                        background: 'linear-gradient(135deg, #52c41a, #73d13d)',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
                                        height: '48px',
                                        fontSize: '16px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Accept
                                </Button>
                                <Button
                                    danger
                                    size="large"
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => handleResponse('declined')}
                                    loading={responding}
                                    style={{ 
                                        minWidth: '140px',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(255, 77, 79, 0.3)',
                                        height: '48px',
                                        fontSize: '16px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Decline
                                </Button>
                            </Space>
                        </div>

                        {/* Login prompt */}
                        {!currentUser && (
                            <Alert
                                message="Login Required"
                                description={
                                    <div style={{ fontSize: '13px' }}>
                                        Please{' '}
                                        <Button 
                                            type="link" 
                                            onClick={() => navigate('/login', { state: { from: `/auctions/${auctionSlug}/invitation/${invitationCode}` } })}
                                            style={{ padding: 0, height: 'auto', fontSize: '13px' }}
                                        >
                                            log in
                                        </Button>
                                        {' '}or{' '}
                                        <Button 
                                            type="link" 
                                            onClick={() => navigate('/register', { state: { from: `/auctions/${auctionSlug}/invitation/${invitationCode}` } })}
                                            style={{ padding: 0, height: 'auto', fontSize: '13px' }}
                                        >
                                            create an account
                                        </Button>
                                        {' '}to respond to this invitation.
                                    </div>
                                }
                                type="warning"
                                showIcon
                                style={{ fontSize: '13px' }}
                            />
                        )}

                        {/* Hint Message */}
                        <div style={{ 
                            background: '#e6f7ff', 
                            padding: '14px', 
                            borderRadius: '8px',
                            border: '1px solid #91d5ff',
                            textAlign: 'center'
                        }}>
                            <Space size="small" style={{ justifyContent: 'center', width: '100%' }}>
                                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                                <Text style={{ fontSize: '13px', color: '#0050b3', fontWeight: '500' }}>
                                    This invitation is unique to you. You can only respond once.
                                </Text>
                            </Space>
                        </div>
                    </Space>
                </div>
            </Card>

            {/* Help Modal */}
            <Modal
                title={
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        padding: '8px 0'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: '#1890ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)'
                        }}>
                            <QuestionCircleOutlined style={{ color: 'white', fontSize: '20px' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', lineHeight: 1.2 }}>
                                Private Auction Invitation Help
                            </div>
                            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                                Quick guide to get started
                            </div>
                        </div>
                    </div>
                }
                open={helpModalVisible}
                onCancel={() => setHelpModalVisible(false)}
                footer={[
                    <Button 
                        key="close" 
                        type="primary" 
                        size="large"
                        onClick={() => setHelpModalVisible(false)}
                        style={{
                            borderRadius: '8px',
                            height: '40px',
                            padding: '0 32px',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)'
                        }}
                    >
                        Got it, thanks!
                    </Button>
                ]}
                width={900}
                style={{ top: 40 }}
                bodyStyle={{ padding: '16px' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Section 1: What is this invitation? */}
                    <div style={{
                        padding: '14px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: '#ffffff'
                    }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                background: '#1890ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <InfoCircleOutlined style={{ color: 'white', fontSize: '16px' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Title level={5} style={{ 
                                    margin: '0 0 6px 0', 
                                    color: '#111827',
                                    fontSize: '15px',
                                    fontWeight: 600
                                }}>
                                    What is this invitation?
                                </Title>
                                <Text style={{ 
                                    fontSize: '13px', 
                                    lineHeight: '1.5', 
                                    color: '#6b7280',
                                    display: 'block'
                                }}>
                                    You've been personally invited to participate in a private auction. This is an exclusive event 
                                    where only invited participants can bid on premium items. Your invitation is unique and 
                                    cannot be transferred to others.
                                </Text>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: How to participate? */}
                    <div style={{
                        padding: '14px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: '#ffffff'
                    }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                background: '#10b981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <CheckCircleOutlined style={{ color: 'white', fontSize: '16px' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Title level={5} style={{ 
                                    margin: '0 0 10px 0', 
                                    color: '#111827',
                                    fontSize: '15px',
                                    fontWeight: 600
                                }}>
                                    How to participate?
                                </Title>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {[
                                        'Accept this invitation to join the auction',
                                        'View auction details to see all available lots',
                                        'Participate in live bidding when the auction starts',
                                        'Win items and complete your purchases'
                                    ].map((text, index) => (
                                        <div key={index} style={{
                                            padding: '8px 10px',
                                            background: '#f9fafb',
                                            borderRadius: '6px',
                                            border: '1px solid #e5e7eb',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                        }}>
                                            <div style={{
                                                width: '22px',
                                                height: '22px',
                                                borderRadius: '6px',
                                                background: '#10b981',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                flexShrink: 0
                                            }}>
                                                {index + 1}
                                            </div>
                                            <Text style={{ fontSize: '12px', color: '#374151', flex: 1, lineHeight: '1.4' }}>
                                                {text}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Important Information */}
                    <div style={{
                        padding: '14px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: '#ffffff'
                    }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                background: '#f59e0b',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <ClockCircleOutlined style={{ color: 'white', fontSize: '16px' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Title level={5} style={{ 
                                    margin: '0 0 10px 0', 
                                    color: '#111827',
                                    fontSize: '15px',
                                    fontWeight: 600
                                }}>
                                    Important Information
                                </Title>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {[
                                        'You can only respond to this invitation once',
                                        'Make sure you\'re logged in before accepting',
                                        'Auction will start at the scheduled time',
                                        'You can view auction details before deciding',
                                        'Accepted invitations can be found in Invitations → Accepted'
                                    ].map((item, index) => (
                                        <div key={index} style={{
                                            padding: '8px 10px',
                                            background: '#f9fafb',
                                            borderRadius: '6px',
                                            border: '1px solid #e5e7eb',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '10px'
                                        }}>
                                            <div style={{
                                                width: '5px',
                                                height: '5px',
                                                borderRadius: '50%',
                                                background: '#f59e0b',
                                                marginTop: '5px',
                                                flexShrink: 0
                                            }} />
                                            <Text style={{ fontSize: '12px', color: '#374151', flex: 1, lineHeight: '1.4' }}>
                                                {item.includes('Invitations') ? (
                                                    <>
                                                        {item.split('Invitations')[0]}
                                                        <strong style={{ color: '#111827' }}>Invitations → Accepted</strong>
                                                    </>
                                                ) : item}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 4: After Accepting */}
                    <div style={{
                        padding: '14px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: '#ffffff'
                    }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                background: '#10b981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <CheckCircleOutlined style={{ color: 'white', fontSize: '16px' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Title level={5} style={{ 
                                    margin: '0 0 10px 0', 
                                    color: '#111827',
                                    fontSize: '15px',
                                    fontWeight: 600
                                }}>
                                    After Accepting
                                </Title>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {[
                                        'Your accepted invitation will be saved in your account',
                                        'You\'ll receive notifications when the auction starts',
                                        'You can access the auction from your dashboard',
                                        'View all your invitations in Invitations → Accepted'
                                    ].map((item, index) => (
                                        <div key={index} style={{
                                            padding: '8px 10px',
                                            background: '#f9fafb',
                                            borderRadius: '6px',
                                            border: '1px solid #e5e7eb',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '10px'
                                        }}>
                                            <CheckCircleOutlined style={{ 
                                                color: '#10b981', 
                                                fontSize: '14px',
                                                marginTop: '2px',
                                                flexShrink: 0
                                            }} />
                                            <Text style={{ fontSize: '12px', color: '#374151', flex: 1, lineHeight: '1.4' }}>
                                                {item.includes('Invitations') ? (
                                                    <>
                                                        {item.split('Invitations')[0]}
                                                        <strong style={{ color: '#111827' }}>Invitations → Accepted</strong>
                                                    </>
                                                ) : item}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AuctionInvitationPage; 